import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, BarChart3, MessageSquare, FolderOpen, X, FileText } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ projects: 0, contacts: 0, testimonials: 0, quotes: 0 });
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [projectForm, setProjectForm] = useState({ title: "", description: "", category: "", images: [], featured: true });
  const [testimonialForm, setTestimonialForm] = useState({ author: "", text: "", rating: 5, featured: true });
  const [imageUrl, setImageUrl] = useState("");

  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    const authHeaders = { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
    try {
      const [statsRes, projectsRes, contactsRes, testimonialsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/projects`),
        axios.get(`${API}/admin/contacts`, { headers: authHeaders }),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/admin/quotes`, { headers: authHeaders })
      ]);
      setStats(statsRes.data);
      setProjects(projectsRes.data);
      setContacts(contactsRes.data);
      setTestimonials(testimonialsRes.data);
      setQuotes(quotesRes.data);
    } catch {
      toast.error("Session expirée");
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    fetchData();
  }, []);

  const addProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/projects`, projectForm, { headers });
      toast.success("Projet ajouté");
      setShowProjectForm(false);
      setProjectForm({ title: "", description: "", category: "", images: [], featured: true });
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const deleteProject = async (id) => {
    try {
      await axios.delete(`${API}/admin/projects/${id}`, { headers });
      toast.success("Projet supprimé");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const addTestimonial = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/testimonials`, testimonialForm, { headers });
      toast.success("Témoignage ajouté");
      setShowTestimonialForm(false);
      setTestimonialForm({ author: "", text: "", rating: 5, featured: true });
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const deleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers });
      toast.success("Témoignage supprimé");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  const addImageUrl = () => {
    if (imageUrl.trim()) {
      setProjectForm(prev => ({ ...prev, images: [...prev.images, imageUrl.trim()] }));
      setImageUrl("");
    }
  };

  const removeImage = (index) => {
    setProjectForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-[#2A2A2A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="font-serif text-xl text-[#F5F5F0]">Administration</h1>
          <span className="font-sans text-xs text-[#E8891C] uppercase tracking-[0.2em]">Paysagement multi-concepts</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="/" className="font-sans text-xs uppercase tracking-[0.15em] text-[#A1A1AA] hover:text-[#E8891C] transition-colors duration-300">
            Voir le site
          </a>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#E8891C] transition-colors duration-300">
            <LogOut size={16} />
            <span className="font-sans text-xs uppercase tracking-[0.15em]">Déconnexion</span>
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          {[
            { label: "Projets", value: stats.projects, icon: FolderOpen },
            { label: "Demandes", value: stats.contacts, icon: MessageSquare },
            { label: "Devis", value: stats.quotes, icon: FileText },
            { label: "Témoignages", value: stats.testimonials, icon: BarChart3 },
          ].map((stat) => (
            <div key={stat.label} className="bg-[#121212] border border-[#2A2A2A] p-6" data-testid={`stat-${stat.label.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-4">
                <stat.icon size={20} className="text-[#E8891C]" />
                <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">{stat.label}</span>
              </div>
              <p className="font-serif text-4xl text-[#F5F5F0]">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="bg-[#121212] border border-[#2A2A2A] rounded-none h-12 p-1">
            <TabsTrigger value="projects" className="rounded-none font-sans text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[#E8891C] data-[state=active]:text-[#0A0A0A]">
              Projets
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-none font-sans text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[#E8891C] data-[state=active]:text-[#0A0A0A]">
              Demandes
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-none font-sans text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[#E8891C] data-[state=active]:text-[#0A0A0A]">
              Témoignages
            </TabsTrigger>
            <TabsTrigger value="quotes" className="rounded-none font-sans text-xs uppercase tracking-[0.15em] data-[state=active]:bg-[#E8891C] data-[state=active]:text-[#0A0A0A]">
              Devis
            </TabsTrigger>
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">Projets</h2>
              <button
                data-testid="add-project-button"
                onClick={() => setShowProjectForm(!showProjectForm)}
                className="flex items-center gap-2 bg-[#E8891C] text-[#0A0A0A] px-4 py-2 font-sans text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#F5A642] transition-colors duration-300"
              >
                <Plus size={14} /> Ajouter
              </button>
            </div>

            {showProjectForm && (
              <form onSubmit={addProject} className="bg-[#121212] border border-[#2A2A2A] p-6 mb-6 space-y-4" data-testid="project-form">
                <Input
                  data-testid="project-title-input"
                  placeholder="Titre du projet"
                  value={projectForm.title}
                  onChange={(e) => setProjectForm(p => ({ ...p, title: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5F0] rounded-none h-10 font-sans"
                  required
                />
                <Textarea
                  data-testid="project-description-input"
                  placeholder="Description"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm(p => ({ ...p, description: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5F0] rounded-none font-sans min-h-[80px]"
                  required
                />
                <Input
                  data-testid="project-category-input"
                  placeholder="Catégorie"
                  value={projectForm.category}
                  onChange={(e) => setProjectForm(p => ({ ...p, category: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5F0] rounded-none h-10 font-sans"
                />
                <div>
                  <div className="flex gap-2">
                    <Input
                      data-testid="project-image-url-input"
                      placeholder="URL de l'image"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5F0] rounded-none h-10 font-sans flex-1"
                    />
                    <button type="button" onClick={addImageUrl} className="bg-[#2A2A2A] text-[#F5F5F0] px-4 h-10 font-sans text-xs uppercase tracking-[0.1em] hover:bg-[#3A3A3A] transition-colors duration-300">
                      Ajouter
                    </button>
                  </div>
                  {projectForm.images.length > 0 && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {projectForm.images.map((img, i) => (
                        <div key={i} className="relative w-20 h-20 bg-[#2A2A2A]">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removeImage(i)} className="absolute -top-1 -right-1 bg-red-900 text-white w-5 h-5 flex items-center justify-center">
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  data-testid="project-submit-button"
                  type="submit"
                  className="bg-[#E8891C] text-[#0A0A0A] px-6 py-2 font-sans text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#F5A642] transition-colors duration-300"
                >
                  Sauvegarder
                </button>
              </form>
            )}

            <div className="space-y-3">
              {projects.map((project) => (
                <div key={project.id} className="bg-[#121212] border border-[#2A2A2A] p-4 flex items-center justify-between" data-testid={`project-item-${project.id}`}>
                  <div className="flex items-center gap-4">
                    {project.images?.[0] && (
                      <img src={project.images[0]} alt="" className="w-16 h-16 object-cover" />
                    )}
                    <div>
                      <h3 className="font-serif text-lg text-[#F5F5F0]">{project.title}</h3>
                      <p className="font-sans text-xs text-[#A1A1AA]">{project.category}</p>
                    </div>
                  </div>
                  <button
                    data-testid={`delete-project-${project.id}`}
                    onClick={() => deleteProject(project.id)}
                    className="text-[#A1A1AA] hover:text-red-500 transition-colors duration-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="mt-6">
            <h2 className="font-serif text-2xl mb-6">Demandes de consultation</h2>
            <div className="space-y-3">
              {contacts.map((contact) => (
                <div key={contact.id} className="bg-[#121212] border border-[#2A2A2A] p-5" data-testid={`contact-item-${contact.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-sans text-sm font-semibold text-[#F5F5F0]">{contact.name}</h3>
                    <span className="font-sans text-xs text-[#A1A1AA]">{new Date(contact.created_at).toLocaleDateString('fr-CA')}</span>
                  </div>
                  <p className="font-sans text-sm text-[#A1A1AA] mb-2">{contact.email} {contact.phone && `| ${contact.phone}`}</p>
                  <p className="font-sans text-sm text-[#F5F5F0]/80">{contact.message}</p>
                </div>
              ))}
              {contacts.length === 0 && (
                <p className="font-sans text-sm text-[#A1A1AA] text-center py-12">Aucune demande pour le moment</p>
              )}
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">Témoignages</h2>
              <button
                data-testid="add-testimonial-button"
                onClick={() => setShowTestimonialForm(!showTestimonialForm)}
                className="flex items-center gap-2 bg-[#E8891C] text-[#0A0A0A] px-4 py-2 font-sans text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#F5A642] transition-colors duration-300"
              >
                <Plus size={14} /> Ajouter
              </button>
            </div>

            {showTestimonialForm && (
              <form onSubmit={addTestimonial} className="bg-[#121212] border border-[#2A2A2A] p-6 mb-6 space-y-4" data-testid="testimonial-form">
                <Input
                  data-testid="testimonial-author-input"
                  placeholder="Nom du client"
                  value={testimonialForm.author}
                  onChange={(e) => setTestimonialForm(t => ({ ...t, author: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5F0] rounded-none h-10 font-sans"
                  required
                />
                <Textarea
                  data-testid="testimonial-text-input"
                  placeholder="Témoignage"
                  value={testimonialForm.text}
                  onChange={(e) => setTestimonialForm(t => ({ ...t, text: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5F0] rounded-none font-sans min-h-[80px]"
                  required
                />
                <button
                  data-testid="testimonial-submit-button"
                  type="submit"
                  className="bg-[#E8891C] text-[#0A0A0A] px-6 py-2 font-sans text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#F5A642] transition-colors duration-300"
                >
                  Sauvegarder
                </button>
              </form>
            )}

            <div className="space-y-3">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-[#121212] border border-[#2A2A2A] p-5 flex items-start justify-between" data-testid={`testimonial-item-${t.id}`}>
                  <div>
                    <p className="font-sans text-sm text-[#F5F5F0]/80 italic mb-2">"{t.text}"</p>
                    <p className="font-sans text-xs text-[#E8891C] font-semibold">{t.author}</p>
                  </div>
                  <button
                    data-testid={`delete-testimonial-${t.id}`}
                    onClick={() => deleteTestimonial(t.id)}
                    className="text-[#A1A1AA] hover:text-red-500 transition-colors duration-300 ml-4 flex-shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes" className="mt-6">
            <h2 className="font-serif text-2xl mb-6">Demandes de devis</h2>
            <div className="space-y-3">
              {quotes.map((q) => (
                <div key={q.id} className="bg-[#121212] border border-[#2A2A2A] p-5" data-testid={`quote-item-${q.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-sans text-sm font-semibold text-[#F5F5F0]">{q.full_name}</h3>
                    <div className="flex items-center gap-3">
                      <span className={`font-sans text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${
                        q.status === "nouveau" ? "bg-[#E8891C]/20 text-[#E8891C]" :
                        q.status === "en_cours" ? "bg-blue-900/30 text-blue-400" :
                        "bg-green-900/30 text-green-400"
                      }`}>{q.status === "nouveau" ? "Nouveau" : q.status === "en_cours" ? "En cours" : "Terminé"}</span>
                      <span className="font-sans text-xs text-[#A1A1AA]">{new Date(q.created_at).toLocaleDateString('fr-CA')}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <p className="font-sans text-xs text-[#A1A1AA]"><span className="text-[#F5F5F0]/60">Tél:</span> {q.phone}</p>
                    <p className="font-sans text-xs text-[#A1A1AA]"><span className="text-[#F5F5F0]/60">Courriel:</span> {q.email}</p>
                  </div>
                  <p className="font-sans text-xs text-[#E8891C] mb-2">{q.service}</p>
                  <p className="font-sans text-sm text-[#F5F5F0]/80">{q.message}</p>
                </div>
              ))}
              {quotes.length === 0 && (
                <p className="font-sans text-sm text-[#A1A1AA] text-center py-12">Aucune demande de devis pour le moment</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
